/**
 * Payment Status Actions Component
 *
 * Provides action buttons for Team Assistants to manage expense report payment status:
 * - Mark as Paid (SUBMITTED -> PAID)
 * - Revert to Submitted (PAID -> SUBMITTED) with confirmation
 */

import { renderIcon } from '../utils/icons';
import type { ExpenseReportStatus } from '../types/expense-tracker';

/**
 * Options for payment status actions component
 */
export interface PaymentStatusActionsOptions {
  status: ExpenseReportStatus;
  isLoading: boolean;
  onMarkAsPaid: () => Promise<void>;
  onRevertToSubmitted: (reason: string) => Promise<void>;
}

/**
 * Show revert confirmation dialog
 */
function showRevertConfirmation(): Promise<string | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4';

    const modal = document.createElement('div');
    modal.className = 'glass-card rounded-2xl p-6 max-w-md w-full';

    modal.innerHTML = `
      <div class="flex items-center gap-3 mb-4">
        <div class="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
          ${renderIcon('alertTriangle', { size: 'md', className: 'text-amber-500' }).outerHTML}
        </div>
        <div>
          <h3 class="text-lg font-semibold text-foreground">Revert Payment Status</h3>
          <p class="text-sm text-muted-foreground">This will undo the payment initiation</p>
        </div>
      </div>

      <p class="text-sm text-muted-foreground mb-4">
        Are you sure you want to revert this expense report back to Submitted status?
        This action will be logged in the status history.
      </p>

      <div class="mb-4">
        <label for="revert-reason" class="block text-sm font-medium text-foreground mb-2">
          Reason for reverting (optional)
        </label>
        <textarea
          id="revert-reason"
          rows="2"
          class="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
          placeholder="e.g., Incorrect amount, wrong expense report selected..."
        ></textarea>
      </div>

      <div class="flex gap-3 justify-end">
        <button id="cancel-btn" class="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors">
          Cancel
        </button>
        <button id="confirm-btn" class="px-4 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors">
          Revert Status
        </button>
      </div>
    `;

    const cleanup = () => overlay.remove();

    const textarea = modal.querySelector('#revert-reason') as HTMLTextAreaElement;

    modal.querySelector('#confirm-btn')?.addEventListener('click', () => {
      const reason = textarea.value.trim();
      cleanup();
      resolve(reason || 'Status reverted by Team Assistant');
    });

    modal.querySelector('#cancel-btn')?.addEventListener('click', () => {
      cleanup();
      resolve(null);
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        cleanup();
        resolve(null);
      }
    });

    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup();
        resolve(null);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Focus the textarea
    setTimeout(() => textarea.focus(), 0);
  });
}

/**
 * Create a loading button
 */
function createLoadingButton(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-muted text-muted-foreground font-medium rounded-lg cursor-not-allowed';
  btn.disabled = true;
  btn.innerHTML = `
    <div class="animate-spin">
      ${renderIcon('loader', { size: 'sm' }).outerHTML}
    </div>
    <span>Processing...</span>
  `;
  return btn;
}

/**
 * Create payment status actions component
 */
export function createPaymentStatusActions(options: PaymentStatusActionsOptions): HTMLElement {
  const container = document.createElement('div');
  container.className = 'glass-card rounded-2xl p-6';

  // Header
  const header = document.createElement('div');
  header.className = 'flex items-center gap-2 mb-4';
  header.innerHTML = `
    ${renderIcon('creditCard', { size: 'md', className: 'text-primary' }).outerHTML}
    <h3 class="text-lg font-semibold text-foreground">Payment Actions</h3>
  `;
  container.appendChild(header);

  // Description
  const description = document.createElement('p');
  description.className = 'text-sm text-muted-foreground mb-4';
  description.textContent = 'As a Team Assistant, you can manage the payment status of this expense report.';
  container.appendChild(description);

  // Actions container
  const actions = document.createElement('div');
  actions.className = 'flex flex-wrap gap-3';

  if (options.isLoading) {
    actions.appendChild(createLoadingButton());
  } else {
    // Mark as Paid button (visible for SUBMITTED reports)
    if (options.status === 'SUBMITTED') {
      const paidBtn = document.createElement('button');
      paidBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors';
      paidBtn.innerHTML = `
        ${renderIcon('checkCircle', { size: 'sm' }).outerHTML}
        <span>Mark as Paid</span>
      `;
      paidBtn.addEventListener('click', async () => {
        try {
          await options.onMarkAsPaid();
        } catch (error) {
          console.error('Failed to mark as paid:', error);
        }
      });
      actions.appendChild(paidBtn);
    }

    // Revert button (for error correction - visible for PAID reports)
    if (options.status === 'PAID') {
      const revertBtn = document.createElement('button');
      revertBtn.className = 'inline-flex items-center gap-2 px-4 py-2 border border-border text-foreground font-medium rounded-lg hover:bg-muted/50 transition-colors';
      revertBtn.innerHTML = `
        ${renderIcon('refresh', { size: 'sm' }).outerHTML}
        <span>Revert to Submitted</span>
      `;
      revertBtn.addEventListener('click', async () => {
        const reason = await showRevertConfirmation();
        if (reason !== null) {
          try {
            await options.onRevertToSubmitted(reason);
          } catch (error) {
            console.error('Failed to revert status:', error);
          }
        }
      });
      actions.appendChild(revertBtn);

      // Status message
      const statusMsg = document.createElement('p');
      statusMsg.className = 'text-sm text-green-500 flex items-center gap-2 w-full';
      statusMsg.innerHTML = `
        ${renderIcon('checkCircle', { size: 'sm' }).outerHTML}
        <span>This expense report has been paid.</span>
      `;
      actions.appendChild(statusMsg);
    }

    if (options.status === 'DRAFT') {
      const statusMsg = document.createElement('p');
      statusMsg.className = 'text-sm text-muted-foreground flex items-center gap-2';
      statusMsg.innerHTML = `
        ${renderIcon('info', { size: 'sm' }).outerHTML}
        <span>This expense report is still a draft. Payment actions will be available after submission.</span>
      `;
      actions.appendChild(statusMsg);
    }

    if (options.status === 'CANCELLED') {
      const statusMsg = document.createElement('p');
      statusMsg.className = 'text-sm text-destructive flex items-center gap-2';
      statusMsg.innerHTML = `
        ${renderIcon('xCircle', { size: 'sm' }).outerHTML}
        <span>This expense report has been cancelled.</span>
      `;
      actions.appendChild(statusMsg);
    }
  }

  container.appendChild(actions);

  return container;
}

export default createPaymentStatusActions;
